import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getOrCreateGuestId } from '../utils/guest-id';
import { randomUUID } from '@ctrl-enter/tools/utils';

export const GUEST_CHAT_LIMIT = 10;

export type GuestMessageStatus = 'sending' | 'processing' | 'streaming' | 'sent' | 'error';

export type GuestMessage = {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: string;
  status: GuestMessageStatus;
};

export type GuestSession = {
  id: string;
  title: string;
  messages: GuestMessage[];
  createdAt: string;
  updatedAt: string;
};

type GuestChatState = {
  sessions: GuestSession[];
  activeSessionId: string | null;
  /** Monotonically increasing — never decremented, even on session delete */
  chatCreationCount: number;
};

type GuestChatActions = {
  createSession(): string | null;
  deleteSession(sessionId: string): void;
  setActiveSession(sessionId: string | null): void;
  addMessage(sessionId: string, message: Omit<GuestMessage, 'id'> & { id?: string }): string;
  updateMessage(sessionId: string, messageId: string, updates: Partial<GuestMessage>): void;
  removeMessage(sessionId: string, messageId: string): void;
  updateSessionTitle(sessionId: string, title: string): void;
};

const buildStorageKey = () => {
  const guestId = getOrCreateGuestId();
  return `ctrl-enter-guest-chats-${guestId}`;
};

export const useGuestChatStore = create<GuestChatState & GuestChatActions>()(
  persist(
    (set, get) => ({
      sessions: [],
      activeSessionId: null,
      chatCreationCount: 0,

      createSession() {
        const { chatCreationCount } = get();
        if (chatCreationCount >= GUEST_CHAT_LIMIT) return null;

        const id = randomUUID();
        const now = new Date().toISOString();
        const newSession: GuestSession = {
          id,
          title: 'New Chat',
          messages: [],
          createdAt: now,
          updatedAt: now,
        };

        set(state => ({
          sessions: [newSession, ...state.sessions],
          activeSessionId: id,
          chatCreationCount: state.chatCreationCount + 1,
        }));

        return id;
      },

      deleteSession(sessionId) {
        set(state => {
          const remaining = state.sessions.filter(s => s.id !== sessionId);
          const nextActiveId =
            state.activeSessionId === sessionId
              ? (remaining[0]?.id ?? null)
              : state.activeSessionId;
          return { sessions: remaining, activeSessionId: nextActiveId };
        });
      },

      setActiveSession(sessionId) {
        set({ activeSessionId: sessionId });
      },

      addMessage(sessionId, message) {
        const id = message.id ?? randomUUID();
        const fullMessage: GuestMessage = { id, ...message };

        set(state => ({
          sessions: state.sessions.map(s =>
            s.id === sessionId
              ? {
                  ...s,
                  messages: [...s.messages, fullMessage],
                  updatedAt: new Date().toISOString(),
                }
              : s
          ),
        }));

        return id;
      },

      updateMessage(sessionId, messageId, updates) {
        set(state => ({
          sessions: state.sessions.map(s =>
            s.id === sessionId
              ? {
                  ...s,
                  messages: s.messages.map(m => (m.id === messageId ? { ...m, ...updates } : m)),
                  updatedAt: new Date().toISOString(),
                }
              : s
          ),
        }));
      },

      removeMessage(sessionId, messageId) {
        set(state => ({
          sessions: state.sessions.map(s =>
            s.id === sessionId
              ? {
                  ...s,
                  messages: s.messages.filter(m => m.id !== messageId),
                }
              : s
          ),
        }));
      },

      updateSessionTitle(sessionId, title) {
        set(state => ({
          sessions: state.sessions.map(s =>
            s.id === sessionId ? { ...s, title, updatedAt: new Date().toISOString() } : s
          ),
        }));
      },
    }),
    {
      name: buildStorageKey(),
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Selector helpers
export const useGuestSessions = () => useGuestChatStore(state => state.sessions);
export const useGuestActiveSessionId = () => useGuestChatStore(state => state.activeSessionId);
export const useGuestChatCreationCount = () => useGuestChatStore(state => state.chatCreationCount);
export const useCanCreateGuestChat = () =>
  useGuestChatStore(state => state.chatCreationCount < GUEST_CHAT_LIMIT);
export const useGuestActiveSession = () =>
  useGuestChatStore(state => state.sessions.find(s => s.id === state.activeSessionId) ?? null);
